<template>
  <div class="relative">
    <!-- 顯示選中的日期 -->
    <div
      @click="showPicker = true"
      class="w-full bg-gray-50 p-4 rounded-xl outline-none focus:ring-2 focus:ring-primary/50 transition-all text-textMain cursor-pointer flex items-center justify-between"
    >
      <span>{{ displayDate }}</span>
      <span class="text-gray-400">📅</span>
    </div>

    <!-- 日期選擇器彈窗 -->
    <Transition name="picker">
      <div
        v-if="showPicker"
        class="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center sm:justify-center sm:p-4"
        @click.self="closePicker"
      >
        <div class="bg-white rounded-t-3xl sm:rounded-3xl w-full sm:max-w-md p-6 pb-safe animate-slide-up">
          <!-- 標題 -->
          <div class="flex items-center justify-between mb-6">
            <h3 class="text-lg font-bold text-textMain">選擇日期</h3>
            <button
              @click="closePicker"
              class="text-textMuted text-2xl leading-none hover:text-textMain transition-colors"
            >
              ✕
            </button>
          </div>

          <!-- 自定義滾輪選擇器 -->
          <div class="mb-6">
            <!-- 標籤行 -->
            <div class="flex gap-1 sm:gap-2 mb-4">
              <div class="flex-1 text-center">
                <label class="text-xl sm:text-2xl font-bold text-textMuted">年</label>
              </div>
              <div class="flex-1 text-center">
                <label class="text-xl sm:text-2xl font-bold text-textMuted">月</label>
              </div>
              <div class="flex-1 text-center">
                <label class="text-xl sm:text-2xl font-bold text-textMuted">日</label>
              </div>
            </div>

            <!-- 滾輪容器 -->
            <div class="flex gap-1 sm:gap-2 relative">
              <!-- 統一的高亮背景 -->
              <div class="absolute inset-x-0 top-1/2 -translate-y-1/2 h-12 sm:h-14 bg-primary/10 rounded-xl pointer-events-none z-10"></div>

              <!-- 年 -->
              <div class="flex-1 relative">
                <div class="relative h-48 sm:h-56 w-full">
                <div
                  ref="yearScroller"
                  class="absolute inset-0 overflow-y-scroll scroll-smooth scrollbar-hide"
                  @scroll="handleYearScroll"
                >
                  <div class="picker-padding">
                    <div
                      v-for="year in years"
                      :key="year"
                      class="picker-item"
                      :class="{ 'text-primary font-bold text-3xl sm:text-4xl': year === selectedYear, 'text-2xl sm:text-3xl': year !== selectedYear }"
                      @click="scrollToYear(year)"
                    >
                      {{ year }}
                    </div>
                  </div>
                </div>
              </div>
            </div>

              <!-- 月 -->
              <div class="flex-1 relative">
                <div class="relative h-48 sm:h-56 w-full">
                <div
                  ref="monthScroller"
                  class="absolute inset-0 overflow-y-scroll scroll-smooth scrollbar-hide"
                  @scroll="handleMonthScroll"
                >
                  <div class="picker-padding">
                    <div
                      v-for="month in 12"
                      :key="month"
                      class="picker-item"
                      :class="{ 'text-primary font-bold text-3xl sm:text-4xl': month === selectedMonth, 'text-2xl sm:text-3xl': month !== selectedMonth }"
                      @click="scrollToMonth(month)"
                    >
                      {{ month }}
                    </div>
                  </div>
                </div>
              </div>
            </div>

              <!-- 日 -->
              <div class="flex-1 relative">
                <div class="relative h-48 sm:h-56 w-full">
                <div
                  ref="dayScroller"
                  class="absolute inset-0 overflow-y-scroll scroll-smooth scrollbar-hide"
                  @scroll="handleDayScroll"
                >
                  <div class="picker-padding">
                    <div
                      v-for="day in daysInMonth"
                      :key="day"
                      class="picker-item"
                      :class="{ 'text-primary font-bold text-3xl sm:text-4xl': day === selectedDay, 'text-2xl sm:text-3xl': day !== selectedDay }"
                      @click="scrollToDay(day)"
                    >
                      {{ day }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            </div>
          </div>

          <!-- 確認按鈕 -->
          <button
            @click="confirmSelection"
            class="w-full bg-primary text-white font-bold py-4 rounded-2xl shadow-lg shadow-primary/30 active:scale-[0.98] transition-all"
          >
            確認
          </button>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick } from 'vue'

interface Props {
  modelValue: string // YYYY-MM-DD 格式
  max?: string // 最大日期 (YYYY-MM-DD)
}

const props = defineProps<Props>()
const emit = defineEmits<{
  'update:modelValue': [value: string]
}>()

const showPicker = ref(false)
const yearScroller = ref<HTMLDivElement>()
const monthScroller = ref<HTMLDivElement>()
const dayScroller = ref<HTMLDivElement>()

// 解析當前日期
const parseDate = (dateStr: string) => {
  const [year, month, day] = dateStr.split('-').map(Number)
  return { year, month, day }
}

const currentDate = computed(() => parseDate(props.modelValue))

// 選中的年月日
const selectedYear = ref(currentDate.value.year)
const selectedMonth = ref(currentDate.value.month)
const selectedDay = ref(currentDate.value.day)

// 更新選中值當 modelValue 改變時
watch(() => props.modelValue, (newVal) => {
  const parsed = parseDate(newVal)
  selectedYear.value = parsed.year
  selectedMonth.value = parsed.month
  selectedDay.value = parsed.day
})

// 生成年份列表（從小到大，與月份、日期一致）
const years = computed(() => {
  const maxYear = props.max ? parseInt(props.max.split('-')[0] || '') : new Date().getFullYear()
  const result = []
  for (let i = maxYear - 10; i <= maxYear; i++) {
    result.push(i)
  }
  return result
})

// 計算該月有多少天
const daysInMonth = computed(() => {
  const year = selectedYear.value ?? new Date().getFullYear()
  const month = selectedMonth.value ?? 1
  return new Date(year, month, 0).getDate()
})

// 當月份變更時，確保日期不超出該月天數
watch([selectedYear, selectedMonth], () => {
  const maxDays = daysInMonth.value
  const currentDay = selectedDay.value
  if (currentDay && currentDay > maxDays) {
    selectedDay.value = maxDays
  }
})

// 顯示格式
const displayDate = computed(() => {
  const { year, month, day } = parseDate(props.modelValue)
  return `${year} 年 ${month} 月 ${day} 日`
})

// 獲取項目高度（響應式）
const getItemHeight = () => {
  // 檢查螢幕寬度是否大於 640px (Tailwind 的 sm 斷點)
  return window.innerWidth >= 640 ? 56 : 48
}

// 當打開選擇器時，滾動到當前選中的值
watch(showPicker, async (isShow) => {
  if (isShow) {
    await nextTick()
    if (selectedYear.value) scrollToYear(selectedYear.value, false)
    if (selectedMonth.value) scrollToMonth(selectedMonth.value, false)
    if (selectedDay.value) scrollToDay(selectedDay.value, false)
  }
})

// 滾動到指定年份
const scrollToYear = (year: number, smooth = true) => {
  if (!yearScroller.value) return
  const index = years.value.indexOf(year)
  if (index === -1) return

  const itemHeight = getItemHeight()
  const scrollTop = index * itemHeight

  yearScroller.value.scrollTo({
    top: scrollTop,
    behavior: smooth ? 'smooth' : 'auto'
  })
  selectedYear.value = year
}

// 滾動到指定月份
const scrollToMonth = (month: number, smooth = true) => {
  if (!monthScroller.value) return

  const itemHeight = getItemHeight()
  const scrollTop = (month - 1) * itemHeight

  monthScroller.value.scrollTo({
    top: scrollTop,
    behavior: smooth ? 'smooth' : 'auto'
  })
  selectedMonth.value = month
}

// 滾動到指定日期
const scrollToDay = (day: number, smooth = true) => {
  if (!dayScroller.value) return

  const itemHeight = getItemHeight()
  const scrollTop = (day - 1) * itemHeight

  dayScroller.value.scrollTo({
    top: scrollTop,
    behavior: smooth ? 'smooth' : 'auto'
  })
  selectedDay.value = day
}

// 處理年份滾動（吸附到最近的項目）
let yearScrollTimeout: number
const handleYearScroll = () => {
  clearTimeout(yearScrollTimeout)
  yearScrollTimeout = window.setTimeout(() => {
    if (!yearScroller.value) return
    const itemHeight = getItemHeight()
    const scrollTop = yearScroller.value.scrollTop
    const index = Math.round(scrollTop / itemHeight)
    const year = years.value[index]
    if (year) {
      scrollToYear(year, true)
    }
  }, 150)
}

// 處理月份滾動
let monthScrollTimeout: number
const handleMonthScroll = () => {
  clearTimeout(monthScrollTimeout)
  monthScrollTimeout = window.setTimeout(() => {
    if (!monthScroller.value) return
    const itemHeight = getItemHeight()
    const scrollTop = monthScroller.value.scrollTop
    const index = Math.round(scrollTop / itemHeight)
    const month = index + 1
    if (month >= 1 && month <= 12) {
      scrollToMonth(month, true)
    }
  }, 150)
}

// 處理日期滾動
let dayScrollTimeout: number
const handleDayScroll = () => {
  clearTimeout(dayScrollTimeout)
  dayScrollTimeout = window.setTimeout(() => {
    if (!dayScroller.value) return
    const itemHeight = getItemHeight()
    const scrollTop = dayScroller.value.scrollTop
    const index = Math.round(scrollTop / itemHeight)
    const day = index + 1
    if (day >= 1 && day <= daysInMonth.value) {
      scrollToDay(day, true)
    }
  }, 150)
}

// 確認選擇
const confirmSelection = () => {
  const year = selectedYear.value
  const month = String(selectedMonth.value).padStart(2, '0')
  const day = String(selectedDay.value).padStart(2, '0')
  const newValue = `${year}-${month}-${day}`

  // 檢查是否超過最大日期
  if (props.max && newValue > props.max) {
    emit('update:modelValue', props.max)
  } else {
    emit('update:modelValue', newValue)
  }

  closePicker()
}

// 關閉選擇器
const closePicker = () => {
  showPicker.value = false
}
</script>

<style scoped>
.picker-enter-active,
.picker-leave-active {
  transition: opacity 0.3s ease;
}

.picker-enter-from,
.picker-leave-to {
  opacity: 0;
}

.animate-slide-up {
  animation: slide-up 0.3s ease;
}

@keyframes slide-up {
  from {
    transform: translateY(100%);
  }
  to {
    transform: translateY(0);
  }
}

/* 隱藏滾動條 */
.scrollbar-hide {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.scrollbar-hide::-webkit-scrollbar {
  display: none;
}

/* 響應式滾輪項目 */
.picker-item {
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #1a1a1a;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

@media (min-width: 640px) {
  .picker-item {
    height: 56px;
  }
}

/* 響應式 padding */
.picker-padding {
  /* 計算: (容器高度 192px / 2) - (項目高度 48px / 2) = 96 - 24 = 72px */
  padding: 72px 0;
}

@media (min-width: 640px) {
  .picker-padding {
    /* 計算: (容器高度 224px / 2) - (項目高度 56px / 2) = 112 - 28 = 84px */
    padding: 84px 0;
  }
}
</style>
